import { useState, useEffect, useRef } from "react";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import AttachmentUpload from "@/components/form/AttachmentUpload";
import { qrProfileService, type QRProfile } from "@/services/qrProfileService";
import { attachmentService } from "@/services/attachmentService";
import { handleApiError, api } from "@/services/api";
import type { Attachment } from "@/types/models";

export default function QRProfileSection() {
  const [profile, setProfile] = useState<QRProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [qrBaseURL, setQrBaseURL] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const [qrPreviewURL, setQrPreviewURL] = useState<string | null>(null);

  const qrFileRef = useRef<HTMLInputElement>(null);
  const [uploadingQR, setUploadingQR] = useState(false);
  const reorderInFlight = useRef(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadQRPreview = (p: QRProfile) => {
    if (p.qr_image_url) {
      setQrPreviewURL(p.qr_image_url);
    }
  };

  const loadProfile = async () => {
    setError("");
    setSuccess("");
    try {
      const p = await qrProfileService.getOrCreate();
      setProfile(p);
      setTitle(p.title || "");
      setDescription(p.description || "");
      setCode(p.code || "");
      setQrBaseURL(p.qr_base_url || "");
      const docs = await attachmentService.listBySource("qr_profile", p.id);
      setAttachments(docs ?? []);
      loadQRPreview(p);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const codeToSend = code !== profile.code ? code : undefined;
      const descToSend = description !== profile.description ? description : undefined;
      const baseToSend = qrBaseURL !== profile.qr_base_url ? qrBaseURL : undefined;
      const updated = await qrProfileService.update(profile.id, {
        title,
        description: descToSend,
        code: codeToSend,
        qr_base_url: baseToSend,
      });
      setProfile(updated);
      setCode(updated.code);
      setDescription(updated.description || "");
      setQrBaseURL(updated.qr_base_url || "");
      setSuccess("QR profile saved.");
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadExistingQR = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    if (qrFileRef.current) qrFileRef.current.value = "";

    setUploadingQR(true);
    setError("");
    try {
      const uploaded = await attachmentService.upload(file);
      // Extract storage key from URL (e.g. "/api/uploads/4/uuid.png" -> "4/uuid.png")
      const key = uploaded.url.replace(/^\/api\/uploads\//, "");
      const updated = await qrProfileService.update(profile.id, {
        title,
        qr_image_key: key,
      });
      setProfile(updated);
      // Show the uploaded QR as the preview
      setQrPreviewURL(uploaded.url);
      setSuccess("QR image uploaded.");
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setUploadingQR(false);
    }
  };

  const handleGenerate = async () => {
    if (!profile) return;
    setUploadingQR(true);
    setError("");
    try {
      // Save domain/slug/title first so the QR encodes the correct URL
      const codeToSend = code !== profile.code ? code : undefined;
      const baseToSend = qrBaseURL !== profile.qr_base_url ? qrBaseURL : undefined;
      if (codeToSend || baseToSend || title !== profile.title) {
        await qrProfileService.update(profile.id, {
          title,
          code: codeToSend,
          qr_base_url: baseToSend,
        });
      }

      const token = localStorage.getItem("accessToken");
      const url = `${api.defaults.baseURL}/qr-profiles/${profile.id}/qr`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();

      // Upload the generated PNG as an attachment
      const file = new File([blob], `qr-${profile.code}.png`, {
        type: "image/png",
      });
      const uploaded = await attachmentService.upload(file);
      const key = uploaded.url.replace(/^\/api\/uploads\//, "");
      const updated = await qrProfileService.update(profile.id, {
        title,
        qr_image_key: key,
      });
      setProfile(updated);
      setQrPreviewURL(uploaded.url);
      setSuccess("QR code generated.");
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setUploadingQR(false);
    }
  };

  const handleMoveDoc = async (id: number, direction: "earlier" | "later") => {
    if (!profile) return;
    // Drop clicks while a reorder is in flight: a second optimistic swap built
    // on unconfirmed state would show the wrong order if the first one reverts.
    if (reorderInFlight.current) return;
    const idx = attachments.findIndex((a) => a.id === id);
    const target = direction === "earlier" ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= attachments.length) return;

    const previous = attachments;
    const next = [...attachments];
    [next[idx], next[target]] = [next[target], next[idx]];
    setAttachments(next);
    reorderInFlight.current = true;
    try {
      await attachmentService.reorder(
        "qr_profile",
        profile.id,
        next.map((a) => a.id)
      );
    } catch (err) {
      setError(handleApiError(err));
      // Resync from the server — on a stale-list 409 the reverted local copy
      // would still be wrong (another device changed the attachments).
      try {
        const docs = await attachmentService.listBySource(
          "qr_profile",
          profile.id
        );
        setAttachments(docs ?? []);
      } catch {
        setAttachments(previous);
      }
    } finally {
      reorderInFlight.current = false;
    }
  };

  const handlePrint = () => {
    if (!qrPreviewURL) return;
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(
        `<html><head><title>QR Code</title><style>body{display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;background:#fff}img{max-width:400px}</style></head><body><img src="${qrPreviewURL}" onload="window.print()" /></body></html>`
      );
      win.document.close();
    }
  };

  const handleDownloadPNG = () => {
    if (!qrPreviewURL || !profile) return;
    const a = document.createElement("a");
    a.href = qrPreviewURL;
    a.download = `qr-${profile.code}.png`;
    a.click();
  };

  const handleDownloadSVG = async () => {
    if (!profile) return;
    const token = localStorage.getItem("accessToken");
    const url = `${api.defaults.baseURL}/qr-profiles/${profile.id}/qr?format=svg`;
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `qr-${profile.code}.svg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setError("Failed to download SVG. Please try again.");
    }
  };

  const publicURL = profile
    ? `${qrBaseURL || window.location.origin}/r/${code || profile.code}`
    : "";

  if (loading) {
    return (
      <div className="py-10 text-center text-gray-500 dark:text-gray-400">
        Loading QR profile...
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-success-50 p-3 text-sm text-success-600 dark:bg-success-500/15 dark:text-success-500">
          {success}
        </div>
      )}

      {/* QR Code Preview */}
      <div className="flex flex-col items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/50">
        {qrPreviewURL ? (
          <img
            src={qrPreviewURL}
            alt="QR Code"
            className="h-48 w-48 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600"
          />
        ) : (
          <div className="flex h-48 w-48 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900">
            <span className="text-center text-xs text-gray-400 px-4">
              No QR code yet
            </span>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-2">
          {!qrPreviewURL && (
            <Button size="sm" onClick={handleGenerate} disabled={uploadingQR}>
              {uploadingQR ? "Generating..." : "Generate QR"}
            </Button>
          )}
          {qrPreviewURL && (
            <>
              <Button size="sm" onClick={handleDownloadPNG}>
                Download PNG
              </Button>
              <Button size="sm" onClick={handleDownloadSVG}>
                Download SVG
              </Button>
              <Button size="sm" variant="outline" onClick={handlePrint}>
                Print
              </Button>
              <Button size="sm" variant="outline" onClick={handleGenerate} disabled={uploadingQR}>
                {uploadingQR ? "Generating..." : "Regenerate"}
              </Button>
            </>
          )}
          <div>
            <input
              ref={qrFileRef}
              type="file"
              accept="image/*"
              onChange={handleUploadExistingQR}
              className="hidden"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => qrFileRef.current?.click()}
              disabled={uploadingQR}
            >
              {uploadingQR ? "Uploading..." : "Upload Existing QR"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-400 break-all text-center">
          {publicURL}
        </p>
      </div>

      {/* Domain + Code + Title */}
      <div>
        <Label htmlFor="qrBaseURL">Customer Domain</Label>
        <Input
          id="qrBaseURL"
          type="text"
          value={qrBaseURL}
          onChange={(e) => setQrBaseURL(e.target.value.replace(/\/+$/, ""))}
          placeholder="e.g. https://floravitafoods.in"
        />
        <p className="mt-1 text-xs text-gray-400">
          The domain used in the QR code URL. Must point to your app. Leave empty to use the default app domain.
        </p>
      </div>

      <div>
        <Label htmlFor="qrCode">QR Code Slug</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 whitespace-nowrap">/r/</span>
          <Input
            id="qrCode"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            placeholder="e.g. my-farm"
          />
        </div>
        <p className="mt-1 text-xs text-gray-400">
          The URL path for your QR page. Use the same slug across environments to keep the same QR.
          Only lowercase letters, numbers, and hyphens.
        </p>
      </div>

      <div>
        <Label htmlFor="qrTitle">QR Page Title</Label>
        <Input
          id="qrTitle"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Yolk Story"
        />
        <p className="mt-1 text-xs text-gray-400">
          Displayed as the heading. If empty, farm name is shown.
        </p>
      </div>

      <div>
        <Label htmlFor="qrDesc">Description</Label>
        <textarea
          id="qrDesc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Quality test reports for the current egg batch"
          rows={2}
          className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
        <p className="mt-1 text-xs text-gray-400">
          Shown below the title on the QR page.
        </p>
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={saving || !code}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Documents */}
      <div>
        <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-white/80">
          Documents shown on QR page
        </h5>
        <p className="mb-3 text-xs text-gray-400">
          Upload PDF reports that will be visible when someone scans the QR
          code. You can add or remove files anytime — the QR code stays the
          same.
        </p>
        <AttachmentUpload
          attachments={attachments}
          onUpload={async (a) => {
            try {
              await api.post(`/attachments/${a.id}/link`, {
                source_type: "qr_profile",
                source_id: profile.id,
              });
              setAttachments((prev) => [...prev, a]);
            } catch (err) {
              setError(handleApiError(err));
            }
          }}
          onDelete={(id) => {
            setAttachments((prev) => prev.filter((att) => att.id !== id));
          }}
          onMove={handleMoveDoc}
        />
      </div>
    </div>
  );
}
